import React, { useEffect, useState } from "react";
import { CreditCard, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getBillingConfig,
  setBillingGateway,
  getPlatformIntegrations,
  updatePlatformIntegration,
} from "../services/api";

type Gateway = "stripe" | "razorpay";

const FIELDS: Record<Gateway, Array<{ key: string; label: string; required: boolean }>> = {
  stripe: [
    { key: "STRIPE_SECRET_KEY", label: "Secret key (sk_...)", required: true },
    { key: "WEBHOOK_ENDPOINT_SECRET", label: "Webhook signing secret (whsec_...)", required: true },
  ],
  razorpay: [
    { key: "RAZORPAY_KEY_ID", label: "Key ID (rzp_...)", required: true },
    { key: "RAZORPAY_KEY_SECRET", label: "Key secret", required: true },
  ],
};

// Master admin's own accounts, separate from the restaurants' POS payment keys.
const PROVIDER_FOR: Record<Gateway, "billingStripe" | "billingRazorpay"> = {
  stripe: "billingStripe",
  razorpay: "billingRazorpay",
};

/**
 * Master admin: choose Stripe or Razorpay for plan (subscription) payments
 * and enter the keys of the account the money should go to.
 */
const PlanPaymentsSettings: React.FC = () => {
  const [gateway, setGateway] = useState<Gateway>("stripe");
  const [savedGateway, setSavedGateway] = useState<Gateway>("stripe");
  const [masked, setMasked] = useState<Record<string, Record<string, string>>>({});
  const [configured, setConfigured] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [billingRes, integrationsRes] = await Promise.all([
        getBillingConfig(),
        getPlatformIntegrations(),
      ]);
      const current = billingRes.data.data.gateway;
      setGateway(current);
      setSavedGateway(current);
      const nextMasked: Record<string, Record<string, string>> = {};
      const nextConfigured: Record<string, boolean> = {};
      (integrationsRes.data?.data || []).forEach((p: any) => {
        nextMasked[p.provider] = p.env || {};
        nextConfigured[p.provider] = !!p.configured;
      });
      setMasked(nextMasked);
      setConfigured(nextConfigured);
    } catch {
      toast.error("Failed to load plan payment settings");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const provider = PROVIDER_FOR[gateway];
  const webhookUrl = `${process.env.REACT_APP_API_URL || ""}/stripe/webhook`;

  const onSave = async () => {
    const credentials = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v.trim()),
    );
    const missing = FIELDS[gateway].filter(
      (f) => f.required && !credentials[f.key] && !masked[provider]?.[f.key],
    );
    if (missing.length) {
      toast.error(`Please enter: ${missing.map((f) => f.label).join(", ")}`);
      return;
    }

    setSaving(true);
    try {
      if (Object.keys(credentials).length) {
        await updatePlatformIntegration(provider, { credentials });
      }
      if (gateway !== savedGateway) {
        await setBillingGateway(gateway);
      }
      toast.success("Plan payment settings saved");
      setForm({});
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mt-6">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard size={28} className="text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800 m-0">Plan Payments</h1>
      </div>
      <p className="text-gray-600 mb-6">
        When restaurant admins buy a plan, the money goes to this account. These keys are only
        for plans — restaurants use their own keys for POS payments.
      </p>

      <div className="mb-5 flex gap-3">
        {(["stripe", "razorpay"] as Gateway[]).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => {
              setGateway(g);
              setForm({});
            }}
            className={`flex-1 rounded-lg border px-4 py-3 text-left transition ${
              gateway === g ? "border-blue-600 bg-blue-50" : "border-gray-300 hover:border-blue-400"
            }`}
          >
            <span className="block font-semibold text-gray-800">
              {g === "stripe" ? "Stripe" : "Razorpay"}
              {savedGateway === g && (
                <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                  In use
                </span>
              )}
            </span>
            <span className="text-xs text-gray-500">
              {g === "stripe"
                ? "Auto-renewing subscriptions"
                : "One-time payment per plan period (UPI, cards)"}
            </span>
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {FIELDS[gateway].map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label} {field.required && "*"}
            </label>
            <input
              type="password"
              autoComplete="off"
              value={form[field.key] || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
              placeholder={masked[provider]?.[field.key] || "Not set"}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ))}
        {gateway === "stripe" && (
          <p className="text-xs text-gray-500">
            In Stripe, add a webhook to <span className="font-mono">{webhookUrl}</span> for
            checkout.session.completed, invoice.paid, invoice.payment_failed and
            customer.subscription.deleted — plans activate from these events.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Status:{" "}
          <span className={configured[provider] ? "text-green-700" : "text-yellow-700"}>
            {configured[provider] ? "Keys saved" : "Keys not saved yet"}
          </span>
          . Leave a field empty to keep its saved value.
        </p>
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        className="mt-5 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
      >
        <Save size={20} />
        {saving ? "Saving..." : "Save Plan Payments"}
      </button>
    </div>
  );
};

export default PlanPaymentsSettings;
